"use client";

import { useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Code2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/providers/auth-provider";
import { formatNumber } from "@/lib/format-datetime";
import { Skeleton } from "@/components/ui/skeleton";
import { formatRuntimeLanguageName } from "@/features/quiz/lib/coding-task-format";
import { useJudgeProblemStats } from "../../hooks";
import type { JudgeProblemStatsRow } from "../../types";

/**
 * The statistics tab, `EX-STA01` (ADR-0085): four figures, the verdicts and
 * the languages, then the three ranked lists.
 *
 * The server withholds every figure below ten judged submissions and the
 * solver count below ten solvers, so this renders what it is given and says
 * why a number is missing rather than printing a zero. Practice only; the
 * view is cached for a few minutes, and the tab says so.
 */
const VERDICT_FILL: Readonly<Record<string, string>> = {
  accepted: "bg-emerald-500",
  wrong_answer: "bg-red-500",
  time_limit_exceeded: "bg-amber-500",
  memory_limit_exceeded: "bg-orange-500",
  runtime_error: "bg-rose-700",
  compilation_error: "bg-neutral-400",
  presentation_error: "bg-sky-500",
};

type ListKey = "fastest" | "lightest" | "shortest";

function measure(row: JudgeProblemStatsRow, list: ListKey, locale: string): string {
  if (list === "fastest") return row.timeMs === null ? "" : `${formatNumber(row.timeMs, locale)} ms`;
  if (list === "lightest") {
    if (row.memoryKb === null) return "";
    return row.memoryKb < 1024
      ? `${formatNumber(row.memoryKb, locale)} KB`
      : `${formatNumber(row.memoryKb / 1024, locale, { maximumFractionDigits: 1 })} MB`;
  }
  return `${formatNumber(row.sourceBytes, locale)} B`;
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3">
      <p className="text-xl font-bold tabular-nums text-neutral-900">{value}</p>
      <p className="mt-0.5 text-xs text-neutral-500">{label}</p>
    </div>
  );
}

export function StatisticsTab({
  slug,
  locale,
  active,
  onOpenProgram,
}: {
  slug: string;
  locale: string;
  active: boolean;
  /** `EX-SRC01`: open a row's program (only rows the server gave an id). */
  onOpenProgram?: (submissionId: string) => void;
}) {
  const t = useTranslations("judge.problem");
  const tJudge = useTranslations("judge");
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const viewerKey = authLoading ? "loading" : isAuthenticated ? "member" : "anon";
  const query = useJudgeProblemStats(slug, active && !authLoading, viewerKey);
  const [list, setList] = useState<ListKey>("fastest");

  if (query.isPending) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" aria-busy="true">
        {[0, 1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-16" />
        ))}
      </div>
    );
  }
  if (query.isError) {
    return <p className="text-sm text-red-700">{t("statsLoadFailed")}</p>;
  }

  const stats = query.data;
  const n = (value: number | null) => (value === null ? "" : formatNumber(value, locale));
  const enough = stats.judged !== null;
  const rows = stats[list];

  return (
    <div className="space-y-6">
      {enough ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Figure label={t("statsSolvers")} value={stats.solvers === null ? t("statsFewerThanTen") : n(stats.solvers)} />
          <Figure label={t("statsAttempters")} value={n(stats.attempters)} />
          <Figure label={t("statsSubmissions")} value={n(stats.judged)} />
          <Figure
            label={t("statsAcceptance")}
            value={formatNumber(stats.acceptanceRate, locale, { style: "percent", maximumFractionDigits: 1 })}
          />
        </div>
      ) : (
        <p className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm leading-relaxed text-neutral-600">
          {t("statsNotEnough")}
        </p>
      )}

      {enough && stats.verdicts.length > 0 ? (
        <section aria-labelledby={`${slug}-verdicts`}>
          <h3 id={`${slug}-verdicts`} className="text-sm font-semibold text-neutral-900">
            {t("statsVerdictsTitle")}
          </h3>
          <div className="mt-2 flex h-3 overflow-hidden rounded-full bg-neutral-100" aria-hidden>
            {stats.verdicts.map((row) => (
              <span
                key={row.verdict}
                className={cn("h-full", VERDICT_FILL[row.verdict] ?? "bg-neutral-300")}
                style={{ width: `${(row.count / (stats.judged ?? 1)) * 100}%` }}
              />
            ))}
          </div>
          <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
            {stats.verdicts.map((row) => (
              <li key={row.verdict} className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2 text-neutral-700">
                  <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", VERDICT_FILL[row.verdict] ?? "bg-neutral-300")} />
                  <span className="truncate">{tJudge(`verdict.${row.verdict}`)}</span>
                </span>
                <span className="tabular-nums text-neutral-900">{n(row.count)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {enough && stats.languages.length > 0 ? (
        <section aria-labelledby={`${slug}-languages`}>
          <h3 id={`${slug}-languages`} className="text-sm font-semibold text-neutral-900">
            {t("statsLanguagesTitle")}
          </h3>
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500">
                <th scope="col" className="py-2 font-medium">{t("statsLanguageCol")}</th>
                <th scope="col" className="py-2 text-right font-medium">{t("statsSubmissionsCol")}</th>
                <th scope="col" className="py-2 text-right font-medium">{t("statsAcceptedCol")}</th>
              </tr>
            </thead>
            <tbody>
              {stats.languages.map((row) => (
                <tr key={row.languageKey} className="border-b border-neutral-100 last:border-0">
                  <td className="py-2 text-neutral-800">{formatRuntimeLanguageName(row.languageKey)}</td>
                  <td className="py-2 text-right tabular-nums text-neutral-800">{n(row.submissions)}</td>
                  <td className="py-2 text-right tabular-nums text-neutral-800">{n(row.accepted)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <section aria-labelledby={`${slug}-lists`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 id={`${slug}-lists`} className="text-sm font-semibold text-neutral-900">
            {t("statsListsTitle")}
          </h3>
          <div role="group" aria-label={t("statsListsTitle")} className="grid w-full grid-cols-3 rounded-lg bg-neutral-100 p-0.5 sm:inline-flex sm:w-auto">
            {(["fastest", "lightest", "shortest"] as const).map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={list === key}
                onClick={() => setList(key)}
                className={cn(
                  "min-h-9 whitespace-nowrap rounded-md px-2 text-xs font-semibold transition-colors duration-150 ease-out sm:px-3",
                  list === key ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500 hover:text-neutral-900",
                )}
              >
                {t(`stats_${key}`)}
              </button>
            ))}
          </div>
        </div>

        {rows.length === 0 ? (
          <p className="mt-3 text-sm text-neutral-500">{t("statsListEmpty")}</p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500">
                <th scope="col" className="whitespace-nowrap py-2 pr-3 font-medium">{t("statsRankCol")}</th>
                <th scope="col" className="w-full py-2 font-medium">{t("statsWhoCol")}</th>
                <th scope="col" className="hidden whitespace-nowrap py-2 pr-4 font-medium sm:table-cell">{t("statsLanguageCol")}</th>
                <th scope="col" className="whitespace-nowrap py-2 text-right font-medium">{t(`stats_${list}_col`)}</th>
                {onOpenProgram ? (
                  <th scope="col" className="py-2">
                    <span className="sr-only">{t("submissionsCodeCol")}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row.username ?? row.name}-${index}`} className="border-b border-neutral-100 last:border-0">
                  <td className="whitespace-nowrap py-2 pr-3 tabular-nums text-neutral-500">{formatNumber(index + 1, locale)}</td>
                  <td className="w-full max-w-0 truncate py-2 pr-3">
                    {row.username ? (
                      <Link href={`/${locale}/u/${row.username}`} className="font-medium text-neutral-900 hover:text-primary">
                        {row.name || row.username}
                      </Link>
                    ) : (
                      <span className="font-medium text-neutral-900">{row.name}</span>
                    )}
                  </td>
                  <td className="hidden whitespace-nowrap py-2 pr-4 text-neutral-600 sm:table-cell">{formatRuntimeLanguageName(row.languageKey)}</td>
                  <td className="whitespace-nowrap py-2 text-right tabular-nums text-neutral-900">{measure(row, list, locale)}</td>
                  {onOpenProgram ? (
                    <td className="w-px py-2 pl-2 text-right">
                      {row.submissionId ? (
                        <button
                          type="button"
                          onClick={() => onOpenProgram(row.submissionId!)}
                          aria-label={t("submissionsViewCodeOf", { name: row.name || row.username || "" })}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
                        >
                          <Code2 className="h-4 w-4" aria-hidden />
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <p className="text-xs text-neutral-400">{t("statsCachedNote")}</p>
    </div>
  );
}
